// Equipment modules (rigid pieces drawn at attachment points by MechView).
// Every piece is authored in the frame of the part that carries it, in the
// neutral upright pose, with the attachment point at the local origin:
//   shoulders  origin = arm pivot, torso frame (module rides above the pauldron)
//   chest      origin = chest mount (just behind the front plate, reactor height)
//   back       origin = outer surface of the backpack; the module extends to −x
//   legs       origin = middle of the shin, shin frame (+y down the shin, +x forward)
// Pieces use followRot so they lean with the torso / swing with the shin.
//
// Each item is a clear silhouette change (DESIGN §6): pods, coil towers and
// shield pauldrons above the shoulders; a bigger chest shell; new reactor
// housings with stacks, masts, fins or jets on the back; thrusters, greaves
// and pistons on the shins.

import { COLORS, mix, shade } from '../../config/palette';
import type { AttachKey, EquipId, EquipSlot, EquipVisual, MechId, TrailKind } from '../../core/types';
import type { Drawer } from './draw';
import type { Bounds } from './sprite';
import type { MechPalette, Ramp } from './styles';

export interface PieceSpec {
  /** Texture part name: key = mechKeys.equip(id, part). */
  part: string;
  bounds: Bounds;
  draw(d: Drawer): void;
}

export interface EquipSpec {
  slot: EquipSlot;
  /** Accent paint colour (tag identity). */
  accent: number;
  pieces: PieceSpec[];
  dashTrail?: TrailKind;
  reactorGlow?: number;
}

const ramp = (shadow: number, base: number, light: number, edge: number): Ramp => ({ shadow, base, light, edge });
const accentRamp = (c: number): Ramp => ramp(shade(c, 0.45), shade(c, 0.7), mix(c, 0xffffff, 0.05), mix(c, 0xffffff, 0.55));

/** Neutral module steel (reads as bolted-on hardware on every mech) + item accent. */
export function equipPalette(spec: EquipSpec): MechPalette {
  return {
    mats: {
      armor: ramp(0x404b5b, 0x667387, 0x8d9bb0, 0xc8d3e0),
      plate: ramp(0x222831, 0x343c48, 0x4d5664, 0x7b8594),
      accent: accentRamp(spec.accent),
      frame: ramp(0x101318, 0x1d222a, 0x2e3540, 0x4c5563),
      metal: ramp(0x3b4049, 0x666d78, 0x9ca4af, 0xdfe5ec),
    },
    glass: 0x10161e,
    reactor: spec.accent,
  };
}

// ───────────────────────────── Shoulders ─────────────────────────────

const missilePod: PieceSpec = {
  part: 'pod',
  bounds: { x0: -12, y0: -24, x1: 15, y1: 0 },
  draw(d) {
    d.plate([[-2.6, -11], [2.6, -11], [2, -1], [-2, -1]], 'frame', { sh: 1 });
    d.plate([[-10, -22], [7, -22.6], [10, -20], [10, -10.6], [-9, -10], [-11.2, -14]], 'armor', { sh: 2.6, hl: 1.1, r: 0.7 });
    d.edge([[-9.6, -21.7], [7, -22.3]]);
    d.plate([[-7.5, -20], [4.5, -20.3], [4.5, -18.3], [-7.5, -18]], 'accent', { sh: 0.5, hl: 0.4, ink: 0.5 });
    d.panel([[-11, -14.2], [9.6, -14.6]]);
    d.bolt(-7.5, -12.2);
    d.bolt(5, -12.4);
    // Launch face with two rows of micro-missile noses.
    d.plate([[8, -22.9], [12.2, -21.2], [12.2, -10.6], [8, -10.2]], 'plate', { sh: 1, hl: 0.6 });
    for (const y of [-18.6, -13.6]) {
      d.plate([[11.6, y - 1.7], [13.6, y - 1], [14.6, y], [13.6, y + 1], [11.6, y + 1.7]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5, r: 0.3 });
      d.lampDisc(10.2, y, 0.9, 0.85, 0.3);
    }
  },
};

const stormCoil: PieceSpec = {
  part: 'coil',
  bounds: { x0: -8.5, y0: -33, x1: 8.5, y1: 0 },
  draw(d) {
    d.plate([[-5.5, -8.5], [5.5, -8.5], [4.2, -1], [-4.2, -1]], 'frame', { sh: 1 });
    d.plate([[-4, -25], [-6.6, -31.4], [-2.6, -26]], 'metal', { sh: 0.6, hl: 0.5, ink: 0.6 });
    d.plate([[4, -25], [6.6, -31.4], [2.6, -26]], 'metal', { sh: 0.6, hl: 0.5, ink: 0.6 });
    d.lampDisc(-6.4, -31, 0.9, 1, 0.3);
    d.lampDisc(6.4, -31, 0.9, 1, 0.3);
    d.plate([[-4.6, -25], [4.6, -25], [5, -8], [-5, -8]], 'plate', { sh: 1.8, hl: 0.8, r: 0.6 });
    d.lampPoly([[-1, -23.4], [1, -23.4], [1, -9.6], [-1, -9.6]], 1, 0.4);
    for (const y of [-21.5, -17, -12.5]) {
      d.plate([[-6.6, y], [6.6, y], [6.6, y + 2.4], [-6.6, y + 2.4]], 'metal', { sh: 0.7, hl: 0.6, ink: 0.6, r: 0.6 });
      d.strip([[-5.2, y + 1.2], [5.2, y + 1.2]], 0.8, 0.85);
    }
    d.plate([[-6, -27], [6, -27], [5, -24.4], [-5, -24.4]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.6 });
  },
};

const aegisPlate: PieceSpec = {
  part: 'plate',
  bounds: { x0: -16, y0: -19, x1: 16.5, y1: 4 },
  draw(d) {
    d.plate([[-14, -8], [-10, -15.4], [2, -17.4], [12, -13.4], [14.8, -6], [12.4, 2], [4, 0], [-4, -2], [-12.4, 0.4]], 'armor', { sh: 3.4, hl: 1.3, r: 0.9 });
    d.edge([[-13.6, -8.2], [-9.8, -15], [2, -17], [11.8, -13]]);
    d.plate([[-9.4, -13.8], [2, -15.6], [10.6, -12.2], [9.4, -10], [2, -12.8], [-8.2, -11.2]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.6 });
    d.panel([[-12.6, -4], [2, -6.6], [13.6, -3]]);
    d.bolt(-8.5, -6);
    d.bolt(8.5, -6.4);
    d.strip([[-9.5, -2.4], [3, -3.8], [11.4, -0.8]], 1.1, 0.95);
  },
};

// ───────────────────────────── Chest ─────────────────────────────

const reactiveShell: PieceSpec = {
  part: 'shell',
  bounds: { x0: -5, y0: -17, x1: 16, y1: 14 },
  draw(d) {
    d.plate([[-3, -14], [7, -15.4], [13, -10.4], [14.8, 0], [12.8, 9], [5, 12.4], [-3, 11.4], [-1, 0]], 'armor', { sh: 3.4, hl: 1.3, r: 0.9 });
    d.edge([[-2.6, -13.8], [7, -15.1], [12.8, -10.2]]);
    // Reactive tile grid.
    d.panel([[-1.6, -7], [13.9, -6.4]]);
    d.panel([[-1.4, 6.2], [13.6, 5.8]]);
    d.panel([[8.6, -14.2], [9.6, 11]]);
    d.plate([[0, -13], [6.6, -14], [7, -8.4], [0.2, -8.2]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
    d.plate([[0.4, 7.2], [7.2, 7.4], [6.8, 10.8], [0.6, 10.2]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.5 });
    d.bolt(11.4, -8.4);
    d.bolt(11.6, 7.4);
    // Window over the mech's own reactor.
    d.disc(3.2, 0, 4.4, 'frame', { sh: 1, hl: 0.7 });
    d.lampDisc(3.2, 0, 2.9, 1, 0.4);
  },
};

const ablativeSlab: PieceSpec = {
  part: 'slab',
  bounds: { x0: -4, y0: -15.5, x1: 16, y1: 12 },
  draw(d) {
    d.plate([[-2, 1.4], [12.6, 1.4], [13.2, 6.2], [9, 10.4], [-2, 10.4]], 'plate', { sh: 2, hl: 0.9, r: 0.6 });
    d.bolt(1, 7.4);
    d.bolt(9.4, 7);
    d.plate([[-2, -6.6], [13, -7], [14.6, -1.2], [13.2, 2.2], [-2, 2.2]], 'armor', { sh: 2.6, hl: 1.1, r: 0.6 });
    d.bolt(1, -1.6);
    d.bolt(11.6, -2);
    d.plate([[-2, -13.2], [10, -13.8], [13, -10.2], [12.2, -6], [-2, -6]], 'armor', { sh: 2.6, hl: 1.2, r: 0.6 });
    d.edge([[-1.6, -13], [10, -13.5]]);
    d.plate([[0.5, -11.8], [9.6, -12.2], [10.4, -10.4], [0.5, -10]], 'accent', { sh: 0.5, hl: 0.4, ink: 0.5 });
    d.slot([[3.6, -0.9], [10.8, -1.2], [10.8, 0.9], [3.6, 1.1]]);
    d.lampPoly([[4.2, -0.5], [10.2, -0.7], [10.2, 0.5], [4.2, 0.7]], 0.9, 0);
  },
};

const capacitorCore: PieceSpec = {
  part: 'core',
  bounds: { x0: -4, y0: -12.5, x1: 15, y1: 11.5 },
  draw(d) {
    d.plate([[-2, -9.4], [8, -10.4], [12.8, -5.4], [12.8, 5.4], [8, 9.4], [-2, 8.4]], 'plate', { sh: 2, hl: 0.9, r: 0.8 });
    d.strip([[-1, -7.2], [3, -5.4]], 0.9, 0.8);
    d.strip([[-1, 6.6], [3, 4.8]], 0.9, 0.8);
    d.disc(6.2, 0, 6.6, 'armor', { sh: 2, hl: 1 });
    d.disc(6.2, 0, 4.6, 'frame', { sh: 0.8, hl: 0.6 });
    d.lampDisc(6.2, 0, 3.4, 1.1, 0.4);
    d.plate([[2, -11.6], [9.6, -11.8], [9.6, -9.8], [2, -9.6]], 'accent', { sh: 0.5, hl: 0.4, ink: 0.5 });
    d.plate([[2, 9.4], [9.6, 9.6], [9.6, 11.4], [2, 11.2]], 'accent', { sh: 0.5, hl: 0.4, ink: 0.5 });
  },
};

// ───────────────────────────── Back ─────────────────────────────

const overdriveHousing: PieceSpec = {
  part: 'housing',
  bounds: { x0: -20.5, y0: -28, x1: 3, y1: 14.5 },
  draw(d) {
    d.plate([[-3, -10], [2, -10], [2, 10], [-3, 10]], 'frame', { sh: 1 });
    // Exhaust stacks (behind the housing).
    d.plate([[-15.6, -11], [-11.2, -11], [-13.2, -24.6], [-18, -24]], 'metal', { sh: 1.2, hl: 0.7 });
    d.plate([[-9.4, -12], [-5.6, -12], [-6.6, -22.6], [-10.8, -22.4]], 'metal', { sh: 1.2, hl: 0.7 });
    d.plate([[-18.6, -24.6], [-12.6, -25.4], [-12.8, -27.2], [-18.6, -26.6]], 'frame', { sh: 0.4, hl: 0.4, ink: 0.6 });
    d.plate([[-11.2, -23], [-6.2, -23.2], [-6.2, -25], [-11.2, -24.8]], 'frame', { sh: 0.4, hl: 0.4, ink: 0.6 });
    d.lampPoly([[-17.8, -25.6], [-13.4, -26.2], [-13.4, -25.6], [-17.8, -25.1]], 1, 0);
    d.lampPoly([[-10.6, -24.1], [-6.8, -24.2], [-6.8, -23.7], [-10.6, -23.6]], 1, 0);
    d.plate([[-16, -12.4], [-4, -13.4], [-0.8, -9], [-0.8, 9], [-4, 13], [-16, 12.2], [-18.6, 0]], 'armor', { sh: 3.4, hl: 1.3, r: 0.9 });
    d.edge([[-15.6, -12.1], [-4, -13.1]]);
    d.plate([[-16.6, -10], [-2, -10.8], [-1.6, -8.6], [-17.2, -7.6]], 'accent', { sh: 0.5, hl: 0.4, ink: 0.5 });
    d.plate([[-14.6, -6.6], [-9.8, -6.8], [-9.8, 8.4], [-14.6, 8.2]], 'frame', { sh: 0.6, hl: 0.5, ink: 0.6 });
    d.lampPoly([[-13.4, -5.4], [-11, -5.5], [-11, 7.2], [-13.4, 7.1]], 1.1, 0);
    d.bolt(-5, -6);
    d.bolt(-5, 7);
    d.panel([[-18, 3.6], [-1, 3.6]]);
  },
};

const tempestMast: PieceSpec = {
  part: 'mast',
  bounds: { x0: -16.5, y0: -36, x1: 3, y1: 12 },
  draw(d) {
    d.plate([[-3, -8], [2, -8], [2, 8], [-3, 8]], 'frame', { sh: 1 });
    d.plate([[-8.4, -10], [-5.8, -10], [-8.6, -30.6], [-11.2, -30.4]], 'metal', { sh: 0.8, hl: 0.6 });
    for (const [y, rx] of [[-14.5, 5.4], [-19.5, 4.6], [-24.5, 3.8]] as const) {
      const cx = -7.1 + (y + 10) * 0.137;
      const ringPath = new Path2D();
      ringPath.ellipse(cx, y, rx, 1.6, -0.12, 0, Math.PI * 2);
      d.shape(ringPath, 'metal', { sh: 0.6, hl: 0.5, ink: 0.7 });
      d.strip([[cx - rx + 1, y + 0.1], [cx + rx - 1, y - 0.6]], 0.8, 0.9);
    }
    d.disc(-10, -31.8, 2.6, 'frame', { sh: 0.6, hl: 0.5 });
    d.lampDisc(-10, -31.8, 1.8, 1.1, 0);
    d.plate([[-12.4, -9.4], [-3, -10.4], [-0.8, -6.4], [-0.8, 8], [-3, 10.2], [-12.4, 9.4], [-14.2, 0]], 'plate', { sh: 2.6, hl: 1, r: 0.8 });
    d.edge([[-12, -9.2], [-3, -10.1]]);
    d.plate([[-13, -3], [-1.4, -3.4], [-1.2, -1], [-13.4, -0.6]], 'accent', { sh: 0.5, hl: 0.4, ink: 0.5 });
    d.lampDisc(-7, 4.4, 2.2, 1, 0.5);
  },
};

const heatFins: PieceSpec = {
  part: 'fins',
  bounds: { x0: -21, y0: -15, x1: 3, y1: 16 },
  draw(d) {
    d.plate([[-3, -11], [2, -11], [2, 11], [-3, 11]], 'frame', { sh: 1 });
    for (let i = 0; i < 5; i++) {
      const y = -12.4 + i * 5.6;
      d.plate([[-5, y], [-17.6, y + 3], [-19.4, y + 5.4], [-5, y + 3.4]], 'metal', { sh: 0.8, hl: 0.6, ink: 0.7, r: 0.4 });
      d.strip([[-17.6, y + 4.2], [-7, y + 2.1]], 0.8, 0.85);
    }
    d.plate([[-6.4, -13.6], [-0.8, -13.6], [-0.8, 13.6], [-6.4, 13.6]], 'plate', { sh: 1.6, hl: 0.8, r: 0.6 });
    d.plate([[-6, -12], [-1.2, -12], [-1.2, -9.6], [-6, -9.6]], 'accent', { sh: 0.5, hl: 0.4, ink: 0.5 });
    d.bolt(-3.6, -5);
    d.bolt(-3.6, 4);
    d.bolt(-3.6, 11);
  },
};

const jumpJets: PieceSpec = {
  part: 'jets',
  bounds: { x0: -23, y0: -14, x1: 3, y1: 19 },
  draw(d) {
    d.plate([[-3, -9], [2, -9], [2, 9], [-3, 9]], 'frame', { sh: 1 });
    d.plate([[-15, -10], [-21.4, -4], [-21.4, 2], [-15, 0.4]], 'accent', { sh: 0.8, hl: 0.6, r: 0.4 });
    d.plate([[-12.6, 9.6], [-6.4, 9.6], [-4.8, 16.2], [-14.2, 16.2]], 'metal', { sh: 1.4, hl: 0.8 });
    d.lampPoly([[-13.6, 15.4], [-5.4, 15.4], [-5, 17.2], [-14, 17.2]], 1.1, 0.4);
    d.plate([[-14, -12], [-5, -12.4], [-2.6, -8], [-2.6, 8], [-6, 10.4], [-13, 10.4], [-15.4, 4]], 'armor', { sh: 3, hl: 1.2, r: 0.8 });
    d.edge([[-13.6, -11.7], [-5, -12.1]]);
    d.panel([[-15, -1], [-3, -1.4]]);
    d.strip([[-12, -8.6], [-12.6, 6]], 1, 0.9);
    d.bolt(-6, -8.4);
  },
};

// ───────────────────────────── Legs (shin frame) ─────────────────────────────

const vectorThruster: PieceSpec = {
  part: 'thruster',
  bounds: { x0: -19, y0: -10, x1: 8, y1: 15 },
  draw(d) {
    d.plate([[-6.4, -2.4], [6.4, -2.8], [6.6, 1], [-6.4, 1.6]], 'frame', { sh: 0.8, hl: 0.5 });
    d.plate([[-13.6, 5], [-9, 8.4], [-12, 13.2], [-17.2, 10]], 'metal', { sh: 1, hl: 0.7 });
    d.lampPoly([[-16.8, 10.6], [-12.6, 13.6], [-11.6, 12.6], [-15.8, 9.6]], 1.1, 0.3);
    d.plate([[-13, -7.4], [-6, -8.2], [-4.4, -3], [-4.4, 6], [-7, 9], [-12, 8.6], [-14.2, 2]], 'armor', { sh: 2.4, hl: 1.1, r: 0.7 });
    d.edge([[-12.6, -7.2], [-6, -7.9]]);
    d.plate([[-14, -6], [-17.4, -9], [-16.6, -3], [-14.2, -1]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.6 });
    d.strip([[-11.4, -4.4], [-11, 5]], 1, 1);
    d.bolt(-6.6, -5.4);
  },
};

const cinderGreave: PieceSpec = {
  part: 'greave',
  bounds: { x0: -5, y0: -11, x1: 12, y1: 14.5 },
  draw(d) {
    d.plate([[-2, -8.4], [7.6, -9.4], [10.2, -2.4], [9.4, 7.6], [6.4, 12.6], [-2, 12.6], [-3.2, 3]], 'armor', { sh: 2.8, hl: 1.1, r: 0.8 });
    d.edge([[-1.6, -8.2], [7.6, -9.1]]);
    d.plate([[-1, -7], [7, -7.8], [7.8, -5.6], [-0.8, -5]], 'accent', { sh: 0.5, hl: 0.4, ink: 0.5 });
    for (const y of [-1.6, 2.6, 6.8]) {
      d.slot([[1.4, y], [8.6, y - 0.6], [8.4, y + 1.8], [1.4, y + 2.2]]);
      d.lampPoly([[2, y + 0.5], [8, y], [7.9, y + 1.3], [2, y + 1.7]], 0.95, 0);
    }
    d.bolt(-0.6, 10.2);
  },
};

const servoPiston: PieceSpec = {
  part: 'piston',
  bounds: { x0: -11, y0: -15, x1: 3, y1: 15.5 },
  draw(d) {
    d.plate([[-7.6, 6], [-5.8, 6], [-5.6, 12.6], [-7.4, 12.6]], 'metal', { sh: 0.4, hl: 0.5, ink: 0.6 });
    d.plate([[-9.4, 11.6], [-4, 11.2], [-3.6, 14.6], [-8.8, 15]], 'frame', { sh: 0.6, hl: 0.5 });
    d.plate([[-9.4, -8], [-5, -8.6], [-4.4, 6.4], [-8.8, 6.8]], 'metal', { sh: 1.2, hl: 0.8, r: 0.5 });
    d.lampPoly([[-8, -4], [-5.8, -4.2], [-5.6, 2.4], [-7.8, 2.6]], 1, 0.4);
    d.plate([[-9.8, -12.8], [-1.6, -13.2], [-1, -8.6], [-9, -7.6]], 'frame', { sh: 0.8, hl: 0.6 });
    d.disc(-1.4, -10.6, 3, 'accent', { sh: 0.8, hl: 0.6 });
    d.disc(-1.4, -10.6, 1.2, 'metal', { sh: 0.3, hl: 0.3, ink: 0.4 });
  },
};

const anchorGreave: PieceSpec = {
  part: 'greave',
  bounds: { x0: -15.5, y0: -12, x1: 13, y1: 17.5 },
  draw(d) {
    d.plate([[-6, 7], [-12, 11], [-14.4, 16.6], [-7, 12.6]], 'metal', { sh: 0.8, hl: 0.6 });
    d.plate([[-7, -9], [8, -10.2], [11.2, -4], [10.6, 8], [7, 12.6], [-5, 12.6], [-8.2, 4]], 'armor', { sh: 3.4, hl: 1.3, r: 0.9 });
    d.edge([[-6.6, -8.8], [8, -9.9]]);
    d.plate([[-6.6, -6.4], [9.4, -7.4], [10, -4.2], [-7, -3.4]], 'accent', { sh: 0.6, hl: 0.5, ink: 0.6 });
    d.panel([[-7.8, 3], [10.8, 2.4]]);
    d.bolt(-4.4, 0);
    d.bolt(7.6, -0.6);
    d.bolt(-3, 9.6);
    d.bolt(6.6, 9.4);
  },
};

// ───────────────────────────── Catalogue ─────────────────────────────

export const EQUIP_SPECS: Readonly<Record<EquipId, EquipSpec>> = {
  reactive_plating: { slot: 'chest', accent: COLORS.explosive, pieces: [reactiveShell] },
  ablative_shell: { slot: 'chest', accent: 0x8a95a5, pieces: [ablativeSlab] },
  capacitor_core: { slot: 'chest', accent: COLORS.electric, pieces: [capacitorCore] },
  missile_pods: { slot: 'shoulders', accent: COLORS.missile, pieces: [missilePod] },
  storm_capacitor: { slot: 'shoulders', accent: COLORS.electric, pieces: [stormCoil] },
  aegis_pauldrons: { slot: 'shoulders', accent: COLORS.shield, pieces: [aegisPlate] },
  overdrive_reactor: { slot: 'back', accent: COLORS.heat, pieces: [overdriveHousing] },
  tempest_reactor: { slot: 'back', accent: COLORS.electric, pieces: [tempestMast] },
  heat_sink_array: { slot: 'back', accent: COLORS.burn, pieces: [heatFins] },
  jump_jets: { slot: 'back', accent: COLORS.dash, pieces: [jumpJets] },
  vector_boosters: { slot: 'legs', accent: COLORS.electric, pieces: [vectorThruster], dashTrail: 'electric' },
  cinder_treads: { slot: 'legs', accent: COLORS.burn, pieces: [cinderGreave], dashTrail: 'burn' },
  servo_striders: { slot: 'legs', accent: 0x6ee0ff, pieces: [servoPiston], dashTrail: 'plasma' },
  anchor_greaves: { slot: 'legs', accent: COLORS.shield, pieces: [anchorGreave] },
};

/** Where each slot's pieces mount: [attach point, layer] (front piece first, mirrored rear piece second). */
export const SLOT_MOUNTS: Readonly<Record<EquipSlot, readonly [AttachKey, 'front' | 'rear'][]>> = {
  chest: [['chest', 'front']],
  shoulders: [
    ['shoulderF', 'front'],
    ['shoulderR', 'rear'],
  ],
  back: [['back', 'rear']],
  legs: [
    ['shinF', 'front'],
    ['shinR', 'rear'],
  ],
};

/**
 * Per-mech fit: piece offset (px in the carrying part's frame) and scale, so
 * one module design sits correctly on all three silhouettes. The generic
 * visual (art.visuals[equipId]) uses VOLT's fit; per-mech variants are
 * registered as art.visuals[`${equipId}@${mechId}`].
 */
export const SLOT_FIT: Readonly<Record<MechId, Record<EquipSlot, { dx: number; dy: number; scale: number }>>> = {
  volt: {
    chest: { dx: 0, dy: 0, scale: 1 },
    shoulders: { dx: 0, dy: 0, scale: 1 },
    back: { dx: 0, dy: 0, scale: 1 },
    legs: { dx: 0, dy: 0, scale: 1 },
  },
  bastion: {
    chest: { dx: 1, dy: 0, scale: 1.18 },
    shoulders: { dx: 0, dy: -3, scale: 1.15 },
    back: { dx: 0, dy: 0, scale: 1.15 },
    legs: { dx: 0, dy: 0, scale: 1.2 },
  },
  wraith: {
    chest: { dx: -0.5, dy: 0, scale: 0.86 },
    shoulders: { dx: 0, dy: 1.5, scale: 0.88 },
    back: { dx: 0, dy: 0, scale: 0.9 },
    legs: { dx: 0, dy: 0, scale: 0.88 },
  },
};

export type { EquipVisual };
